import { getPrimaryLanguageSubtag } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';
import { camelCaseObject } from '@edx/frontend-platform/utils';
import { memoize } from 'lodash-es';
import { COURSE_STATUSES } from '../constants';
import dayjs from './dayjs';

export const isCourseEnded = endDate => dayjs(endDate).isBefore(dayjs());

export const createArrayFromValue = (value) => {
  const values = [];

  if (Array.isArray(value)) {
    return value;
  }

  values.push(value);
  return values;
};

export const isDefined = (value) => {
  const values = createArrayFromValue(value);
  return values.every(item => item !== undefined);
};

export const isNull = (value) => {
  const values = createArrayFromValue(value);
  return values.every(item => item === null);
};

export const isDefinedAndNotNull = (value) => {
  const values = createArrayFromValue(value);
  return values.every(item => isDefined(item) && !isNull(item));
};

export const isDefinedAndNull = (value) => {
  const values = createArrayFromValue(value);
  return values.every(item => isDefined(item) && isNull(item));
};

export const hasTruthyValue = (value) => {
  const values = createArrayFromValue(value);
  return values.every(item => !!item);
};

export const sumOfArray = (values) => (values.every(item => typeof item === 'number' && !Number.isNaN(item))
  ? values.reduce((prev, next) => prev + next, 0)
  : null);

export const hasValidStartExpirationDates = ({ startDate, endDate }) => {
  const now = dayjs();
  return now.isBetween(startDate, endDate);
};

export const fixedEncodeURIComponent = (str) => encodeURIComponent(str).replace(/[!()*]/g, (c) => `%${ c.charCodeAt(0).toString(16)}`);

export const formatStringAsNumber = (str, radix = 10) => {
  // converts a string into a number and format it with separated commas
  const num = parseInt(str, radix);
  return num.toLocaleString();
};

export const getPrimaryPartnerLogo = (partnerDetails) => {
  if (!partnerDetails.primaryPartner || !partnerDetails.showPartnerLogo) {
    return undefined;
  }
  return {
    src: partnerDetails.primaryPartner.logoImageUrl,
    alt: partnerDetails.primaryPartner.name,
  };
};

export const getContactEmail = (config) => {
  const adminEmails = config.adminUsers.map(user => user.email);
  if (config.contactEmail) { return config.contactEmail; }
  if (adminEmails.length >= 1) { return adminEmails; }
  return null;
};

/**
 * Given an error, returns the status code from the custom attributes (Axios error)
 * or the standard JS error response.
 * @param {Error} error An error object
 * @returns {number} The status code (e.g., 404)
 */
export function getErrorResponseStatusCode(error) {
  return error.customAttributes?.httpErrorStatus || error.response?.status;
}

/**
 * Logs a react-query query error message on failure, if present.
 * @param {Error} error An error object
 * @param {Query} query The query object
 * @returns {void}
 */
export function queryCacheOnErrorHandler(error, query) {
  if (query.meta?.errorMessage) {
    logError(query.meta?.errorMessage);
  }
}

/**
 * Determines whether a React Query query should be retried on failure.
 *
 * @param {number} failureCount The number of times the query has failed
 * @param {Error} error The error that caused the query to fail
 * @returns {boolean} Whether the query should be retried
 */
export function defaultQueryClientRetryHandler(failureCount, error) {
  if (failureCount >= 3 || getErrorResponseStatusCode(error) === 404) {
    return false;
  }
  return true;
}

/**
 * Given a CSS variable name, returns the computed value of the CSS variable.
 * @param {string} cssVariableName A string representing a CSS variable.
 * @returns {string} The computed value of the CSS variable.
 */
export function getComputedStylePropertyCSSVariable(cssVariableName) {
  return getComputedStyle(document.documentElement).getPropertyValue(cssVariableName);
}

/**
 * Returns the brand colors from the CSS variables.
 * @returns {Object} The brand colors from the CSS variables.
 */
export function getBrandColorsFromCSSVariables() {
  return {
    primary: getComputedStylePropertyCSSVariable('--pgn-color-primary'),
    info100: getComputedStylePropertyCSSVariable('--pgn-color-info-100'),
    info500: getComputedStylePropertyCSSVariable('--pgn-color-info-500'),
    white: getComputedStylePropertyCSSVariable('--pgn-color-white'),
    dark: getComputedStylePropertyCSSVariable('--pgn-color-dark'),
  };
}

/**
 * Helper function utilizing dayjs's 'isBetween' function to determine
 * if the date passed is between today and an offset amount of days
 *
 * @param date
 * @param days
 * @returns {boolean}
 */
export function isTodayWithinDateThreshold({ date, days }) {
  const dateToCheck = dayjs(date);
  const today = dayjs();
  const offsetDays = dateToCheck.subtract(days, 'days');
  return today.isBetween(offsetDays, dateToCheck);
}

export function isTodayBetweenDates({ startDate, endDate }) {
  const today = dayjs();
  const formattedStartDate = dayjs(startDate);
  const formattedEndDate = dayjs(endDate);
  return today.isBetween(formattedStartDate, formattedEndDate);
}

/**
 * Returns a formatted date in the following format:
 *
 * Passed Date: 2024-07-18T16:00:00Z
 *
 * Formatted Date: July 18, 2024
 *
 * The following parameters can be overridden to modify the date:
 *  - year
 *  - month
 *  - day
 *
 *  See {@link https://formatjs.io/docs/react-intl/api/#formatdate} for information
 * @param intl
 * @param timestamp
 * @param formatOpts
 * @returns {String}
 */
export function i18nFormatTimestamp({ intl, timestamp, formatOpts = {} }) {
  return intl.formatDate(timestamp, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...formatOpts,
  });
}

export const formatPrice = (price, options = {}) => {
  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
  return USDollar.format(Math.abs(price));
};

export function findCourseStatusKey(statusValue) {
  for (const key in COURSE_STATUSES) {
    if (COURSE_STATUSES[key] === statusValue) {
      return key;
    }
  }
  return undefined;
}

export const memoizedCamelCaseObject = memoize(camelCaseObject);

/**
 * Salesforce language codes for region-specific locales, which must be matched on the full
 * locale: resolving these by language subtag alone would pick the wrong variant ("zh-HK" would
 * become Simplified "zh_CN", and "pt-PT" would become Brazilian "pt_BR"). Only locales the app
 * actually ships are listed; see src/i18n/messages for the supported set.
 */
const HELP_CENTER_LANGUAGE_BY_LOCALE = {
  'pt-pt': 'pt_PT',
  'zh-hk': 'zh_TW',
};

/**
 * Salesforce language codes for locales whose code differs from the locale's primary language
 * subtag. Locales matching neither map use the subtag as-is (e.g. "es-419" -> "es").
 */
const HELP_CENTER_LANGUAGE_BY_LANGUAGE_SUBTAG = {
  en: 'en_US',
  pt: 'pt_BR',
  zh: 'zh_CN',
};

/**
 * Returns the given help.edx.org article URL with its `language` query parameter set to the
 * learner's current locale, so linked support articles open in the learner's language.
 *
 * Only for help.edx.org, which honors `language`. It is deliberately NOT applied to the
 * enterprise Help Center (enterprise-support.edx.org): multilingual is not configured in that
 * Salesforce Experience Cloud site, so it strips the parameter and always serves English.
 * Applying this helper there would produce a redirect without changing the language. Today the
 * only caller is the course pacing help link; see ENT-12344.
 *
 * English is set explicitly rather than omitted, because Salesforce persists a language
 * preference per session: without the parameter an English learner can be served a page in a
 * language they previously viewed.
 *
 * The URL is returned unchanged if it is empty or cannot be parsed.
 *
 * @param {string} url A help.edx.org article URL (e.g., LEARNER_SUPPORT_PACED_COURSE_MODE_URL).
 * @param {string} locale The learner's current locale (e.g., `intl.locale`).
 * @returns {string}
 */
export function getLocalizedHelpCenterUrl(url, locale) {
  if (!url || !locale) {
    return url;
  }
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return url;
  }
  const languageSubtag = getPrimaryLanguageSubtag(locale).toLowerCase();
  const helpCenterLanguage = HELP_CENTER_LANGUAGE_BY_LOCALE[locale.toLowerCase()]
    ?? HELP_CENTER_LANGUAGE_BY_LANGUAGE_SUBTAG[languageSubtag]
    ?? languageSubtag;
  parsedUrl.searchParams.set('language', helpCenterLanguage);
  return parsedUrl.toString();
}
